import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  DialogBody,
  DialogSurface,
  DialogTitle,
  mergeClasses,
  Button
} from '@fluentui/react-components';
import { MessageBar, MessageBarTitle, MessageBarBody } from '@fluentui/react-message-bar';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { ClipchampPlayer } from '../player';
import { previewClipchampVideoModalStyles } from './style';
import type { PreviewClipchampVideoModalProps } from '../types';

export const PreviewClipchampVideoModal = ({
  i18n,
  videoUrl,
  show,
  onClose,
  logger,
  loadClipchampPlayer,
  onShowChange,
  isAnimationProcessing,
}: PreviewClipchampVideoModalProps) => {
  const DismissIcon = useMemo(() => <Button icon={<Dismiss20Regular />} aria-label={i18n.getString(_TL_('Cancel'))} onClick={onClose} className="clipchamp-player-modal-close-btn" />, [i18n, onClose]);

  const classes = previewClipchampVideoModalStyles();

  return (
    <Dialog
      open={show}
      {...({
        className: mergeClasses('preview-clipchamp-video-dialog', classes.dialog),
        onDismiss: onClose,
      } as unknown as Record<string, unknown>)}
      onOpenChange={(event, data) => {
        // it is the users responsibility to react accordingly to the open state change
        if (onShowChange) {
          onShowChange(data.open);
        }
      }}
    >
      <DialogSurface className={mergeClasses('preview-clipchamp-video-dialog', classes.dialog)}>
        <DialogBody>
          <DialogTitle action={DismissIcon}>
            {i18n.getString(_TL_('Video Preview'))}
          </DialogTitle>
          <DialogContent className={classes.dialogContent} >
            <div className={classes.dialogContentContainer}>
              <ClipchampPlayer videoUrl={videoUrl} width="100%" logger={logger} loadClipchampPlayer={loadClipchampPlayer} i18n={i18n} />
            </div>
            { isAnimationProcessing && (
            <MessageBar className={classes.messageBar}>
              <MessageBarBody>
                <MessageBarTitle>{i18n.getString(_TL_('Working on asset'))}</MessageBarTitle>
                {i18n.getString(_TL_('We are processing your image into a video asset. This can take a few minutes, and this preview will change once complete.'))}
              </MessageBarBody>
            </MessageBar>
            )
            }
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

PreviewClipchampVideoModal.propTypes = {
  videoUrl: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logError: PropTypes.func,
  }).isRequired,
  loadClipchampPlayer: PropTypes.func.isRequired,
  onShowChange: PropTypes.func,
  isAnimationProcessing: PropTypes.bool.isRequired,
};

PreviewClipchampVideoModal.defaultProps = {
  onShowChange: null,
};
